import { AxiosError } from 'axios';
import { useBranchContext } from 'providers/BranchProvider';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { toastConfig } from 'utils/toastConfig';
import { TProcurementReturns } from 'utils/types';
import * as Yup from 'yup';

interface RejectReturnDetailsFormProps {
  procurementReturnId: number;
}

export type RejectReturnDetailsFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

const RejectReturnDetailsForm = forwardRef<
  RejectReturnDetailsFormHandle,
  RejectReturnDetailsFormProps
>(({ procurementReturnId }, ref) => {
  const [returnDetails, setReturnDetails] = useState({
    rejectReason: ''
  });
  const { selectedBranch } = useBranchContext();

  const rejectReturnSchema = Yup.object().shape({
    rejectReason: Yup.string().required('Reason is required')
  });

  const handleSubmit = async () => {
    try {
      await rejectReturnSchema.validate(returnDetails, { abortEarly: false });
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/procurementReturns/${procurementReturnId}/reject`,
        { rejectReason: returnDetails.rejectReason }
      );
      toast.success('Return Rejected', {
        autoClose: 2000,
        ...toastConfig
      });
      return true;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined
        });
      }
    }
  };

  const handleFormChange = <K extends keyof TProcurementReturns>(
    field: K,
    value: TProcurementReturns[K]
  ) => {
    setReturnDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }));

  return (
    <>
      <Form className="w-100">
        <Row className="mb-3">
          <Form.Label>Reject Reason</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={returnDetails.rejectReason}
            onChange={e => handleFormChange('rejectReason', e.target.value)}
          />
        </Row>
      </Form>
    </>
  );
});

export default RejectReturnDetailsForm;
